import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { EmojiProvider } from '@atlaskit/emoji/types';
import EmojiResource from '@atlaskit/emoji/emoji-resource';

import type { RemixFeedbackMetadata } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';

import { expValEquals } from '@confluence/feature-experiments';
import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import type { CustomGreetingConfig } from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import type { PageMode } from '@confluence/page-utils/entry-points/enums.ts';

import { createNavigateToObject } from '../utils/navigation.ts';
import type { FeedbackVariant } from '../utils/FeedbackVariant.ts';
import { useLazyShowFeedbackFlag } from '../utils/useLazyShowFeedbackFlag.ts';

const CLOSE_MODAL_DELAY = 500;

export const useConversationAssistantConfig = ({
	assistanceServiceConfigHeaders = {},
	preventAbortRequestsOnStagingModalClose = false,
	preventAbortRequestsOnStandaloneChatClose = false,
}: {
	assistanceServiceConfigHeaders?: Record<string, string>;
	preventAbortRequestsOnStagingModalClose?: boolean;
	preventAbortRequestsOnStandaloneChatClose?: boolean;
}): {
	displaySubmitFlag: boolean;
	siteId: string;
	product: string;
	orgId: string | undefined;
	assistanceServiceConfig: {
		headers: Record<string, string>;
		baseUrl: string;
	};
	emojiProvider: Promise<EmojiProvider>;
	userId: string | undefined;
	preventAbortRequestsOnStagingModalClose: boolean;
	preventAbortRequestsOnStandaloneChatClose: boolean;
} => {
	const { cloudId, orgId, userId } = useSessionData();
	const emojiProvider = useMemo(() => {
		return getEmojiProvider(EmojiResource, {
			cloudId,
			userId,
		});
	}, [userId, cloudId]);

	const assistanceServiceConfig = {
		headers: assistanceServiceConfigHeaders,
		baseUrl: '/gateway/api/assist',
	};
	// Keep the config object identity stable when its semantic inputs are stable.
	const memoizedAssistanceServiceConfig = useMemo(
		() => ({
			headers: assistanceServiceConfigHeaders,
			baseUrl: '/gateway/api/assist',
		}),
		[assistanceServiceConfigHeaders],
	);
	const selectedAssistanceServiceConfig = expValEquals(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		true,
	)
		? memoizedAssistanceServiceConfig
		: assistanceServiceConfig;

	const memoizedConfig = useMemo(
		() => ({
			displaySubmitFlag: true,
			siteId: cloudId,
			product: 'confluence',
			orgId,
			assistanceServiceConfig: selectedAssistanceServiceConfig,
			emojiProvider,
			userId: userId ?? undefined,
			preventAbortRequestsOnStagingModalClose,
			preventAbortRequestsOnStandaloneChatClose,
		}),
		[
			cloudId,
			emojiProvider,
			orgId,
			preventAbortRequestsOnStagingModalClose,
			preventAbortRequestsOnStandaloneChatClose,
			selectedAssistanceServiceConfig,
			userId,
		],
	);

	return expValEquals('rovo_ttai_segment_render_stabilization', 'isEnabled', true)
		? memoizedConfig
		: {
				displaySubmitFlag: true,
				siteId: cloudId,
				product: 'confluence',
				orgId,
				assistanceServiceConfig: selectedAssistanceServiceConfig,
				emojiProvider,
				userId: userId ?? undefined,
				preventAbortRequestsOnStagingModalClose,
				preventAbortRequestsOnStandaloneChatClose,
			};
};

export const useConversationAssistantUiConfig = ({
	flags,
	customGreeting,
	shouldShowGreeting,
	shouldHideFeedback,
}: {
	flags: FlagsStateContainer;
	customGreeting?: CustomGreetingConfig;
	shouldShowGreeting?: boolean;
	shouldHideFeedback?: boolean;
}): {
	customHandlers: {
		handleSubmitSmartCreate: ({
			baseUrl,
			path,
		}: {
			baseUrl: string;
			path: string;
		}) => Promise<void>;
		handleCloseSmartCreate: ({
			feedbackVariant,
			currentConversationId,
			requestId,
			remixMetadata,
			contentType,
			contentMode,
		}: {
			feedbackVariant?: FeedbackVariant;
			currentConversationId?: string | null;
			requestId?: string;
			remixMetadata?: RemixFeedbackMetadata;
			contentType?: string;
			contentMode?: PageMode;
		}) => void;
	};
	elements: {
		voiceDictationButton: boolean;
		shouldShowGreeting: boolean | undefined;
	};
	customGreeting: CustomGreetingConfig | undefined;
} => {
	const { push } = useRouteActions();
	const intl = useIntl();
	const { showModal } = useDialogs();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();

	const navigateToObject = useCallback(
		async ({ baseUrl: baseUrlNav, path }: { baseUrl: string; path: string }) => {
			createNavigateToObject(push)({ baseUrl: baseUrlNav, path });
			// artifical delay before closing the modal gives the app a moment to transition to the created object while the modal is still open, this creates a smoother transition from modal to new object.
			// For non-SPA transitions, a full page reload will happen so this delay will be ignored
			await new Promise((r) => setTimeout(r, CLOSE_MODAL_DELAY));
		},
		[push],
	);

	const showCreateWithRovoModalCloseFeedbackFlag = useLazyShowFeedbackFlag(
		flags,
		intl,
		showModal,
		createAnalyticsEvent,
	);

	const handleCloseSmartCreate = useCallback(
		({
			feedbackVariant,
			currentConversationId,
			requestId,
			remixMetadata,
			contentType,
			contentMode,
		}: {
			feedbackVariant?: FeedbackVariant;
			currentConversationId?: string | null;
			requestId?: string;
			remixMetadata?: RemixFeedbackMetadata;
			contentType?: string;
			contentMode?: PageMode;
		}) => {
			if (shouldHideFeedback) {
				return;
			}

			if (feedbackVariant === undefined) {
				return;
			}

			void showCreateWithRovoModalCloseFeedbackFlag({
				feedbackVariant,
				contentType,
				contentMode,
				cloudId,
				currentConversationId: currentConversationId ?? undefined,
				requestId,
				remixMetadata,
			});
		},
		[showCreateWithRovoModalCloseFeedbackFlag, shouldHideFeedback, cloudId],
	);

	// Keep uiConfig identity stable when handler/greeting inputs are stable.
	const memoizedUiConfig = useMemo(
		() => ({
			customHandlers: {
				handleSubmitSmartCreate: navigateToObject,
				handleCloseSmartCreate,
			},
			elements: {
				voiceDictationButton: true,
				shouldShowGreeting: shouldShowGreeting ?? undefined,
			},
			customGreeting: customGreeting ?? undefined,
		}),
		[customGreeting, handleCloseSmartCreate, navigateToObject, shouldShowGreeting],
	);

	return expValEquals('rovo_ttai_segment_render_stabilization', 'isEnabled', true)
		? memoizedUiConfig
		: {
				customHandlers: {
					handleSubmitSmartCreate: navigateToObject,
					handleCloseSmartCreate,
				},
				elements: {
					voiceDictationButton: true,
					shouldShowGreeting: shouldShowGreeting ?? undefined,
				},
				customGreeting: customGreeting ?? undefined,
			};
};
